// Scheduled Reports API (reportsMS, plan 4.3). Everything under /reports, envelope {success, message, payload}.
import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "./requestInterceptor";

const request = (url, method, token, body) => ({
  url,
  method,
  headers: {
    "Content-Type": "application/json",
    "x-access-token": token,
  },
  ...(body ? { body } : {}),
});

const reportTags = (id) => ["Reports", { type: "Report", id }, { type: "ReportHistory", id }];

// NOTE: no createApi-level refetchOnMountOrArgChange: preview is costly, and the configuration is read by
// the navbar on every page. A failed save invalidates nothing, so an open form is never re-read under it;
// a refused list action always does, so the list shows the true state.
export const scheduledReportsApi = createApi({
  reducerPath: "scheduledReportsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ReportConfig", "Reports", "Report", "ReportHistory"],
  endpoints: (builder) => ({
    getReportsConfig: builder.query({
      query: ({ token }) => request("reports/config", "GET", token),
      keepUnusedDataFor: 900,
      providesTags: ["ReportConfig"],
    }),
    getReports: builder.query({
      query: ({ token }) => request("reports/", "GET", token),
      providesTags: ["Reports"],
    }),
    getReport: builder.query({
      query: ({ token, id }) => request(`reports/${id}`, "GET", token),
      providesTags: (result, error, { id }) => [{ type: "Report", id }],
    }),
    getReportHistory: builder.query({
      query: ({ token, id }) => request(`reports/${id}/history`, "GET", token),
      providesTags: (result, error, { id }) => [{ type: "ReportHistory", id }],
    }),
    createReport: builder.mutation({
      query: ({ token, body }) => request("reports/", "POST", token, body),
      invalidatesTags: (result, error) => (error ? [] : ["Reports"]),
    }),
    updateReport: builder.mutation({
      query: ({ token, id, body }) => request(`reports/${id}`, "PUT", token, body),
      invalidatesTags: (result, error, { id }) => (error ? [] : reportTags(id)),
    }),
    deleteReport: builder.mutation({
      query: ({ token, id }) => request(`reports/${id}`, "DELETE", token),
      invalidatesTags: ["Reports"],
    }),
    pauseReport: builder.mutation({
      query: ({ token, id }) => request(`reports/${id}/pause`, "POST", token),
      invalidatesTags: (result, error, { id }) => reportTags(id),
    }),
    resumeReport: builder.mutation({
      query: ({ token, id }) => request(`reports/${id}/resume`, "POST", token),
      invalidatesTags: (result, error, { id }) => reportTags(id),
    }),
    checkRecipients: builder.mutation({
      query: ({ token, body }) => request("reports/recipient-check", "POST", token, body),
    }),
    previewReport: builder.mutation({
      query: ({ token, body }) => request("reports/preview", "POST", token, body),
    }),
    testSendReport: builder.mutation({
      query: ({ token, body }) => request("reports/test-send", "POST", token, body),
    }),
  }),
});

export const {
  useGetReportsConfigQuery,
  useGetReportsQuery,
  useGetReportQuery,
  useGetReportHistoryQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  usePauseReportMutation,
  useResumeReportMutation,
  useCheckRecipientsMutation,
  usePreviewReportMutation,
  useTestSendReportMutation,
} = scheduledReportsApi;
